import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, Phone, MapPin, Loader2, ArrowLeft, Inbox, SlidersHorizontal, Check, TrendingUp } from "lucide-react";

const STATUS = {
  pending_pro: { label: "Recherche de professionnels en cours", color: "bg-amber-100 text-amber-800" },
  distributed: { label: "Transmise à des professionnels", color: "bg-sky-100 text-sky-800" },
  closed: { label: "Clôturée", color: "bg-slate-100 text-slate-600" },
  suspended: { label: "Suspendue", color: "bg-red-100 text-red-700" },
  realized: { label: "Travaux réalisés", color: "bg-emerald-100 text-emerald-700" },
  expired: { label: "Expirée — à relancer", color: "bg-orange-100 text-orange-700" },
};

export default function QuoteTracking() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(undefined);
  const [showAdjust, setShowAdjust] = useState(false);
  const [adj, setAdj] = useState({ budget: "", extra_radius_km: 0 });
  const [saving, setSaving] = useState(false);
  const [reach, setReach] = useState(null);

  const load = () => api.get(`/devis/requests/track/${token}`).then((r) => {
    setData(r.data);
    setAdj({ budget: r.data?.request?.budget || "", extra_radius_km: r.data?.request?.extra_radius_km || 0 });
  }).catch(() => setData(null));

  useEffect(() => { load(); api.get(`/devis/requests/track/${token}/reach-estimate`).then((r) => setReach(r.data)).catch(() => {}); }, [token]);
  useEffect(() => { if (params.get("adjust") === "1") setShowAdjust(true); }, [params]);

  const extraFor = (km) => reach?.steps?.find((s) => s.km === km)?.extra ?? 0;

  const submitAdjust = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.patch(`/devis/requests/track/${token}/adjust`, {
        budget: adj.budget,
        extra_radius_km: adj.extra_radius_km,
        flexible_zone: adj.extra_radius_km > 0,
      });
      toast.success(res.assigned_count > 0
        ? `Demande mise à jour : ${res.assigned_count} nouveau(x) professionnel(s) contacté(s) !`
        : "Demande mise à jour. Nous élargissons la recherche de professionnels.");
      setShowAdjust(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (data === undefined) return <div className="p-20 text-center text-slate-400"><Loader2 className="animate-spin inline" /> Chargement…</div>;
  if (data === null) return <div className="p-20 text-center text-slate-500" data-testid="track-notfound">Demande introuvable.</div>;

  const { request: r, responses, pros } = data;
  const st = STATUS[r.status] || STATUS.pending_pro;

  return (
    <div className="min-h-screen bg-[#F2F4F5]" data-testid="quote-tracking-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> Accueil</Link>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Demande {r.ref}</div>
              <h1 className="font-heading text-2xl font-extrabold text-[#172A3A] mt-1">{r.trade_name}{r.subneed ? ` · ${r.subneed}` : ""}</h1>
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-1"><MapPin size={14} /> {r.city} {r.postal_code && `(${r.postal_code})`}</div>
            </div>
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${st.color}`} data-testid="track-status">{st.label}</span>
          </div>
          <p className="mt-4 text-slate-700 whitespace-pre-line">{r.description}</p>
          {r.urgency_label && <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-amber-700 bg-amber-50 rounded-full px-3 py-1"><Clock size={13} /> {r.urgency_label}</div>}
        </div>

        {["pending_pro", "distributed", "expired"].includes(r.status) && (
          <div className="mt-6 bg-white rounded-2xl border border-orange-200 shadow-sm p-6" data-testid="track-adjust-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-heading text-lg font-bold text-[#172A3A] flex items-center gap-2"><SlidersHorizontal size={18} className="text-[#E05638]" /> Élargir ma zone / mon budget</h2>
                <p className="text-sm text-slate-500 mt-0.5">Recevez plus de devis sans refaire votre demande.</p>
              </div>
              {!showAdjust && (
                <button onClick={() => setShowAdjust(true)} data-testid="track-adjust-open"
                  className="h-10 px-5 rounded-xl bg-[#E05638] text-white font-semibold text-sm hover:bg-[#c94a2f]">Ajuster ma demande</button>
              )}
            </div>
            {showAdjust && (
              <div className="mt-4 space-y-4" data-testid="track-adjust-form">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-700">Budget indicatif</span>
                  <input value={adj.budget} onChange={(e) => setAdj((a) => ({ ...a, budget: e.target.value }))}
                    placeholder="ex. : jusqu'à 500 €" data-testid="track-adjust-budget"
                    className="mt-1 w-full h-11 px-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#E05638]" />
                </label>
                <div>
                  <span className="text-sm font-semibold text-slate-700">Élargir mon rayon de recherche</span>
                  <p className="text-xs text-slate-500 mt-0.5">Acceptez des professionnels un peu plus éloignés pour recevoir plus de devis.</p>
                  <div className="mt-3 grid grid-cols-4 gap-2" data-testid="track-radius-stepper">
                    {[0, 10, 20, 30].map((km) => {
                      const active = adj.extra_radius_km === km;
                      const extra = extraFor(km);
                      return (
                        <button
                          key={km}
                          type="button"
                          onClick={() => setAdj((a) => ({ ...a, extra_radius_km: km }))}
                          data-testid={`track-radius-${km}`}
                          className={`flex flex-col items-center justify-center rounded-xl border py-2.5 px-1 transition-colors ${active ? "border-[#E05638] bg-orange-50 text-[#E05638]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                          <span className="text-sm font-bold">{km === 0 ? "Ma zone" : `+${km} km`}</span>
                          {km > 0 && (
                            <span className={`text-[11px] font-semibold mt-0.5 ${active ? "text-[#E05638]" : "text-emerald-600"}`} data-testid={`track-radius-extra-${km}`}>
                              {extra > 0 ? `+${extra} pro${extra > 1 ? "s" : ""}` : "—"}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {adj.extra_radius_km > 0 && extraFor(adj.extra_radius_km) > 0 && (
                  <div className="flex items-start gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-3 py-2" data-testid="track-reach-estimate">
                    <TrendingUp size={16} className="mt-0.5 shrink-0" />
                    <span>En élargissant à <strong>+{adj.extra_radius_km} km</strong>, <strong>~{extraFor(adj.extra_radius_km)} professionnel{extraFor(adj.extra_radius_km) > 1 ? "s" : ""} supplémentaire{extraFor(adj.extra_radius_km) > 1 ? "s" : ""}</strong> pourrai{extraFor(adj.extra_radius_km) > 1 ? "ent" : "t"} voir votre demande.</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={submitAdjust} disabled={saving} data-testid="track-adjust-submit"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#172A3A] text-white font-bold text-sm hover:bg-[#223c52] disabled:opacity-50">
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Mettre à jour ma demande
                  </button>
                  <button onClick={() => setShowAdjust(false)} className="h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuler</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pros sélectionnés */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="font-heading text-lg font-bold text-[#172A3A] mb-3">Professionnels sélectionnés ({pros.length})</h2>
          {pros.length === 0 ? (
            <p className="text-sm text-slate-500">Nous recherchons les professionnels correspondant à votre besoin dans votre secteur. Vous serez prévenu(e) par e-mail dès qu'un professionnel répond.</p>
          ) : (
            <div className="space-y-2">
              {pros.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#F2F4F5]" data-testid="track-pro">
                  <div className="w-9 h-9 rounded-full bg-[#172A3A] text-white grid place-items-center font-bold text-sm shrink-0">{(p.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-[#172A3A] truncate">{p.name}</div><div className="text-xs text-slate-500">{p.city}</div></div>
                  <span className="text-xs text-slate-400">{p.status === "accepted" ? "A répondu" : "Contacté"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Réponses */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="font-heading text-lg font-bold text-[#172A3A] mb-3 flex items-center gap-2"><Inbox size={18} className="text-[#E05638]" /> Propositions reçues ({responses.length})</h2>
          {responses.length === 0 ? (
            <p className="text-sm text-slate-500">Aucune proposition pour l'instant.</p>
          ) : (
            <div className="space-y-3">
              {responses.map((resp, i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-150" data-testid="track-response">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="font-semibold text-[#172A3A]">{resp.business?.name}</div>
                    {resp.price_estimate && <span className="text-sm font-bold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">{resp.price_estimate}</span>}
                  </div>
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{resp.message}</p>
                  {resp.business?.phone && (
                    <a href={`tel:${resp.business.phone}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E05638]"><Phone size={14} /> {resp.business.phone}</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
